<script lang="ts" setup>
import type { ButtonVariants } from '~ds/components/ui/button';
import type { UnifiedChatContentRecommendation } from '~kui/types/contentRecommendation';
import type { RouteLocationRaw } from 'vue-router';

import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

type CollectionRec = Extract<UnifiedChatContentRecommendation, { type: 'learning_asset_collection' }>;

interface Props {
  recs: CollectionRec[];
  heading?: string;
  description?: string;
  ctaTo: string;
  ctaText?: string;
  ctaVariant?: ButtonVariants['variant'];
  extraText?: string | null;
  /**
   * Builds each card's link destination from its rec. Provided by the container (which owns
   * the app route constants) so the cards render as crawlable `<a>` links; when unset the
   * cards stay buttons driven by the `cardClick` event.
   */
  cardTo?: (rec: CollectionRec) => string | RouteLocationRaw;
  /** AU-only Coin Rush promo pill on the collection cards; gated on the coin-rush-enabled flag by the container. */
  coinRush?: boolean;
}

withDefaults(defineProps<Props>(), {
  ctaText: undefined,
  ctaVariant: 'default',
  extraText: null,
  cardTo: undefined,
  coinRush: false,
})

defineEmits<{
  cardClick: [slug: string];
}>();

const { t } = useI18n();
</script>

<template>
  <div class="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-24">
    <div class="col-span-1 lg:col-span-2">
      <div class="flex flex-col gap-9">
        <Heading size="2xl" as="h2">
          {{ heading ?? t('unified.learn.trending.heading') }}
        </Heading>
        <slot>
          <Text
            v-if="description"
            size="lg"
            class="text-foreground"
          >
            {{ description }}
          </Text>
          <Text v-if="extraText" size="lg">
            {{ extraText }}
          </Text>
        </slot>
        <div class="hidden lg:block">
          <Button
            size="lg"
            :variant="ctaVariant"
            as-child
          >
            <NuxtLink :to="ctaTo">
              {{ ctaText ?? t('unified.learn.trending.cta') }}
            </NuxtLink>
          </Button>
        </div>
      </div>
    </div>
    <div class="col-span-1 lg:col-span-3 flex flex-col gap-4">
      <div class="grid grid-cols-2 sm:grid-cols-2 gap-4">
        <motion.div
          v-for="(rec, i) in recs"
          :key="rec.id"
          :initial="{ opacity: 0, y: 40 }"
          :while-in-view="{ opacity: 1, y: 0 }"
          :viewport="{ once: true, margin: '0px 0px -80px 0px' }"
          :transition="{ type: 'spring', stiffness: 260, damping: 20, delay: i * 0.08 }"
        >
          <KUIUnifiedAssetCollectionCard
            :allow-partner-variant="false"
            :rec="rec"
            :to="cardTo ? cardTo(rec) : undefined"
            :coin-rush="coinRush"
            variant="full"
            @click="$emit('cardClick', rec.slug)"
          />
        </motion.div>
      </div>
      <div class="lg:hidden flex justify-center items-center">
        <Button
          size="lg"
          :variant="ctaVariant"
          class="mt-8"
          as-child
        >
          <NuxtLink :to="ctaTo">
            {{ ctaText ?? t('unified.learn.trending.cta') }}
          </NuxtLink>
        </Button>
      </div>
    </div>
  </div>
</template>
